import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, X, Heart, Sparkles, Send } from "lucide-react";
import { useScrollBehavior } from "@/hooks/useScrollBehavior";
import { supabase } from "@/integrations/supabase/client";

type PromptStage = "hidden" | "question" | "capture" | "thanks";

const STORAGE_KEY = "sprankelien_lead_prompt";
const DISMISS_KEY = "sprankelien_lead_dismissed";

/** Pages where the prompt should NOT appear */
const EXCLUDED_PATHS = ["/admin", "/inschrijven", "/contact"];

const SmartLeadPrompt = () => {
  const { scrollProgress, isIdle } = useScrollBehavior();
  const [stage, setStage] = useState<PromptStage>("hidden");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  // Don't show on excluded pages or if already dismissed/submitted
  const isExcluded = EXCLUDED_PATHS.some((p) =>
    window.location.pathname.startsWith(p)
  );

  useEffect(() => {
    if (isExcluded) return;
    const dismissed = sessionStorage.getItem(DISMISS_KEY);
    const submitted = localStorage.getItem(STORAGE_KEY);
    if (dismissed || submitted) return;

    // Trigger: user has scrolled past 45% and paused (idle)
    if (scrollProgress > 0.45 && isIdle && !hasTriggered) {
      setHasTriggered(true);
      // Small delay so it feels natural, not reactive
      const timer = setTimeout(() => setStage("question"), 800);
      return () => clearTimeout(timer);
    }
  }, [scrollProgress, isIdle, hasTriggered, isExcluded]);

  const dismiss = useCallback(() => {
    setStage("hidden");
    sessionStorage.setItem(DISMISS_KEY, "1");
  }, []);

  const handleInterest = useCallback(() => {
    setStage("capture");
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim()) return;
      setSubmitting(true);

      try {
        // Store the lead - simple insert into a leads table
        // Falls back gracefully if table doesn't exist yet
        await supabase.from("leads" as any).insert({
          name: name.trim() || null,
          email: email.trim(),
          source: window.location.pathname,
        } as any);
      } catch {
        // Silent fail — the UX continues regardless
      }

      localStorage.setItem(STORAGE_KEY, "1");
      setSubmitting(false);
      setStage("thanks");

      // Auto-dismiss after showing thanks
      setTimeout(() => setStage("hidden"), 6000);
    },
    [email, name]
  );

  if (isExcluded) return null;

  return (
    <AnimatePresence>
      {stage !== "hidden" && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 0.68, 0.35, 1] }}
          className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-[380px] z-50"
        >
          <div className="relative rounded-2xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-[var(--shadow-floating)] overflow-hidden">
            {/* Warm ambient glow behind the card */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,hsl(32_35%_52%_/_0.04)_0%,transparent_60%)] pointer-events-none" />

            {/* Close button */}
            <button
              onClick={dismiss}
              className="absolute top-3.5 right-3.5 p-1.5 rounded-full text-muted-foreground/40 hover:text-muted-foreground/70 hover:bg-muted/30 transition-all duration-500 z-10"
              aria-label="Sluiten"
            >
              <X className="h-3.5 w-3.5" />
            </button>

            <div className="relative p-6 sm:p-7">
              {/* Stage: Question — conversational prompt */}
              {stage === "question" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 0.68, 0.35, 1] }}
                >
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="p-1.5 rounded-full bg-accent/8">
                      <Heart className="h-3.5 w-3.5 text-accent/60" />
                    </div>
                    <span className="text-[10px] font-body font-semibold uppercase text-accent/60 tracking-[0.25em]">
                      Even tussendoor
                    </span>
                  </div>
                   <p className="text-[15px] font-heading italic text-foreground leading-relaxed mb-2">
                    Fijn dat je hier bent
                  </p>
                  <p className="text-[13px] text-muted-foreground/60 font-body leading-[1.7] mb-6">
                    Wil je af en toe een tip ontvangen over kinderbegeleiding?
                    Geschreven met zorg, niet met haast.
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleInterest}
                      className="group flex items-center justify-between w-full px-5 py-3 rounded-xl bg-primary/[0.03] border border-border/40 hover:border-accent/20 hover:bg-accent/[0.04] transition-all duration-600 text-left"
                    >
                      <span className="text-[13px] font-body text-foreground/80 font-medium">
                        Ja, houd me op de hoogte
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-accent/50 group-hover:translate-x-1 transition-transform duration-500" />
                    </button>
                    <Link
                      to="/contact"
                      onClick={dismiss}
                      className="group flex items-center justify-between w-full px-5 py-3 rounded-xl bg-primary/[0.03] border border-border/40 hover:border-accent/20 hover:bg-accent/[0.04] transition-all duration-600 text-left"
                    >
                      <span className="text-[13px] font-body text-foreground/80 font-medium">
                        Liever direct contact
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-accent/50 group-hover:translate-x-1 transition-transform duration-500" />
                    </Link>
                  </div>
                </motion.div>
              )}

              {/* Stage: Capture — email form */}
              {stage === "capture" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 0.68, 0.35, 1] }}
                >
                  <div className="flex items-center gap-2.5 mb-4">
                    <div className="p-1.5 rounded-full bg-accent/8">
                      <Sparkles className="h-3.5 w-3.5 text-accent/60" />
                    </div>
                    <span className="text-[10px] font-body font-semibold uppercase text-accent/60 tracking-[0.25em]">
                      Blijf verbonden
                    </span>
                  </div>
                  <p className="text-[13.5px] text-muted-foreground/60 font-body leading-[1.7] mb-5">
                    Ontvang af en toe een inspirerend bericht over
                    kinderbegeleiding. Geen spam, beloofd.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      type="text"
                      placeholder="Je naam (optioneel)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2.5 text-[13px] font-body bg-secondary/60 border border-border/40 rounded-xl text-foreground placeholder:text-muted-foreground/35 focus:outline-none focus:border-accent/30 focus:shadow-[0_0_0_3px_hsl(32_35%_52%_/_0.06)] transition-all duration-500"
                    />
                    <input
                      type="email"
                      required
                      placeholder="Je e-mailadres"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2.5 text-[13px] font-body bg-secondary/60 border border-border/40 rounded-xl text-foreground placeholder:text-muted-foreground/35 focus:outline-none focus:border-accent/30 focus:shadow-[0_0_0_3px_hsl(32_35%_52%_/_0.06)] transition-all duration-500"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group w-full flex items-center justify-center gap-2.5 px-5 py-3 rounded-xl bg-primary text-primary-foreground text-[13px] font-body font-semibold hover:opacity-90 transition-all duration-500 disabled:opacity-50"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {submitting ? "Even geduld..." : "Verstuur"}
                    </button>
                    <p className="text-[10.5px] text-muted-foreground/35 font-body text-center leading-relaxed">
                      Je kunt je altijd uitschrijven
                    </p>
                  </form>
                </motion.div>
              )}

              {/* Stage: Thanks — confirmation with next step */}
              {stage === "thanks" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 0.68, 0.35, 1] }}
                  className="text-center py-2"
                >
                  <div className="inline-flex p-2.5 rounded-full bg-accent/8 mb-4">
                    <Heart className="h-4 w-4 text-accent/60" />
                  </div>
                  <p className="text-[15px] font-heading italic text-foreground mb-2">
                    Dankjewel!
                  </p>
                  <p className="text-[13px] text-muted-foreground/60 font-body leading-[1.7] mb-5">
                    We houden je op de hoogte. Ondertussen kun je gerust
                    rondkijken.
                  </p>
                  <Link
                    to="/over-mij"
                    onClick={dismiss}
                    className="inline-flex items-center gap-2 text-[11px] font-semibold text-accent/70 font-body uppercase tracking-[0.12em] group hover:text-accent transition-colors duration-600"
                  >
                    Leer mij beter kennen
                    <ArrowRight className="h-3.5 w-3.5 transition-all duration-600 group-hover:translate-x-1.5" />
                  </Link>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SmartLeadPrompt;
